import moment from 'moment'
import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import { LOGIC_UNIT } from '.'
import I18n from '../../../../core/i18n'
import { adminService, utilService } from '../../../../core/services'
import { COLORS, BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import { InputDate, InputRadio, InputText } from '../../../atoms'
import AdminLayoutEdit from '../_layouts/edit'
import * as interfaces from '../../../../static/interfaces'

declare var window: interfaces.IWindow

const FIELDS = {
  name: 'name',
  comment: 'comment',
  expire_at: 'expire_at',
  logic_type: 'logic_type',
  logic_value: 'logic_value',
  limit: 'limit',
  usage: 'usage',
}
const OPTIONS = [
  { id: '0', label: 'Price discount', value: 'fixed' },
  { id: '1', label: 'Percentage discount', value: 'percentage' },
]
const title = I18n.t('admin.edit', {
  model: I18n.t('coupon', { scope: 'activerecord.models' }),
})
const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: COLORS.infoBG,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: COLORS.white,
  }),
}
const AdminCouponEdit: React.FC<{ coupon: any; categories: any }> = props => {
  const { data } = utilService.getDataFromJson(props.coupon)
  const [logicLabel, setLogicLabel] = React.useState<string>(LOGIC_UNIT[data.logic_type])
  const { data: couponCategories } = utilService.getDataFromJson(props.categories)
  const [selectedCategories, setSelectedCategories] = React.useState(
    data.categories.map(category => {
      const categoryData = couponCategories.find(item => String(item.id) === String(category.id))
      return { value: String(categoryData.id), label: categoryData.name }
    })
  )
  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const result = { ...values }
      result.category_ids = selectedCategories?.map(category => Number(category.value)) || []
      const { updatedCoupon, flush } = await adminService.updateCoupon(data.id, result)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = '/admin/coupons'
    },
    [selectedCategories]
  )
  const handleCalendarOutSideRange = React.useCallback(calendarMoment => {
    return calendarMoment.isBefore(moment())
  }, [])

  return (
    <AdminLayoutEdit
      model="coupon"
      title={title}
      fields={FIELDS}
      indexLink="/admin/coupons"
      formItems={
        <FormWrapper>
          <InputText required={true} name="name" defaultValue={data.name} label="Name" />
          <InputText required={false} name="comment" defaultValue={data.comment} label="Commnet" />
          <S.ReactSelectWrapper>
            <p className="ReactSelect_Title">Category</p>
            <div className="ReactSelect_Main">
              <ReactSelect
                placeholder="Select Category"
                options={couponCategories.map(category => ({
                  value: String(category.id),
                  label: category.name,
                }))}
                isMulti={true}
                onChange={setSelectedCategories}
                styles={selectCustomStyles}
                defaultValue={selectedCategories}
                menuPortalTarget={document.querySelector('body')}
              />
            </div>
          </S.ReactSelectWrapper>
          <InputDate
            name="expire_at"
            defaultValue={data.expire_at}
            label="Expire"
            required={true}
            isOutsideRange={handleCalendarOutSideRange}
          />
          <InputText required={true} name="limit" defaultValue={data.limit} label="Limit" />
          <LogicWrap>
            <p className="Logic_Title">Logic</p>
            <div className="Logic_InputWrapper">
              <div className="Logic_Input">
                <span>Type: </span>
                <InputRadio
                  required={true}
                  name="logic_type"
                  directionRow={true}
                  options={OPTIONS}
                  defaultValue={data.logic_type}
                  onChangeHandler={e => {
                    setLogicLabel(LOGIC_UNIT[e.target.value])
                  }}
                />
              </div>
              <div className="Logic_Input">
                <span>Value: </span>
                <InputText required={true} name="logic_value" defaultValue={data.logic_value} />
                <span> {logicLabel}</span>
              </div>
            </div>
          </LogicWrap>
          <div className="Hidden">
            <InputText
              required={true}
              readonly={true}
              name="usage"
              defaultValue="0"
              label="Usage"
            />
          </div>
        </FormWrapper>
      }
      handleSubmit={handleSubmit}
    />
  )
}
const S: { [key: string]: AnyStyledComponent } = {}
const FormWrapper = styled.div`
  .Hidden {
    display: none;
  }
`
const LogicWrap = styled.div`
  display: flex;
  margin: 24px 0;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .Logic_Title {
    min-width: 240px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .Logic_InputWrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    .Logic_Input {
      display: flex;
      align-items: center;
    }
  }
`

S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 240px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`

export default AdminCouponEdit
